import axios from "axios";
import { message } from "antd";
import { API } from "../config/config";

export const api = axios.create({
  baseURL: API,
});

const getToken = () => localStorage.getItem("token");

api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      localStorage.clear();
      message.error({
        content: "Сессия истекла. Пожалуйста, войдите снова.",
        onClose: () => {
          window.location.href = "/login";
        },
      });
    }
    if (error.response && error.response.status === 503 && error.response.data?.maintenanceMode) {
      // Не редиректим админов, они должны иметь доступ
      const authState = localStorage.getItem("authState");
      if (authState) {
        try {
          const parsed = JSON.parse(authState);
          if (parsed.user?.role === "admin") {
            // Админы могут продолжать работу
            return Promise.reject(error);
          }
        } catch (e) {
          // Игнорируем ошибки парсинга
        }
      }
      // Редиректим всех остальных на страницу обслуживания
      if (window.location.pathname !== "/maintenance") {
        window.location.href = "/maintenance";
      }
      return Promise.reject(error);
    }
    return Promise.reject(error);
  }
);

function getFilenameFromContentDisposition(
  contentDisposition: string | undefined,
  fallback: string
): string {
  if (!contentDisposition) return fallback;

  const pickLastPathPart = (name: string) => name.split(/[/\\]/).pop() || name;
  const stripQuotes = (value: string) => {
    const v = value.trim();
    if ((v.startsWith('"') && v.endsWith('"')) || (v.startsWith("'") && v.endsWith("'"))) {
      return v.slice(1, -1);
    }
    return v;
  };

  // RFC 5987: filename*=UTF-8''...
  const filenameStarMatch = contentDisposition.match(/filename\*\s*=\s*([^;]+)/i);
  if (filenameStarMatch?.[1]) {
    let v = stripQuotes(filenameStarMatch[1]);
    v = v.replace(/^UTF-8''/i, "");
    try {
      v = decodeURIComponent(v);
    } catch {
      // ignore bad encoding, use raw value
    }
    v = pickLastPathPart(v);
    if (v) return v;
  }

  // Basic: filename="..." or filename=...
  const filenameMatch = contentDisposition.match(/filename\s*=\s*([^;]+)/i);
  if (filenameMatch?.[1]) {
    const v = pickLastPathPart(stripQuotes(filenameMatch[1]));
    if (v) return v;
  }

  return fallback;
}

// API функция для скачивания Excel файла с деталями оборота
export const downloadTurnoverDetailsExcel = async (comparisonDate?: string) => {
  try {
    const params: any = {};
    if (comparisonDate) {
      params.comparisonDate = comparisonDate;
    }

    const response = await api.get("/stats/turnover/excel", {
      responseType: "blob",
      params,
    });
    
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    
    // Получаем имя файла из заголовков ответа или используем значение по умолчанию
    const contentDisposition = response.headers["content-disposition"];
    const filename = getFilenameFromContentDisposition(contentDisposition, "turnover_details.xlsx");
    
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    message.success("Excel файл успешно скачан!");
  } catch (error) {
    console.error("Error downloading turnover Excel:", error);
    message.error("Ошибка при скачивании Excel файла");
    throw error;
  }
};

// API функция для получения списка сохраненных Excel отчетов по обороту (admin-only)
export const listTurnoverDetailsExcelReports = async (): Promise<
  Array<{ date: string; fileName: string; sizeBytes: number; createdAtMs: number }>
> => {
  try {
    const response = await api.get("/stats/turnover/excel/reports");
    return response.data.reports || [];
  } catch (error) {
    console.error("Error listing turnover Excel reports:", error);
    message.error("Ошибка при получении списка отчетов");
    throw error;
  }
};

// API функция для скачивания сохраненного Excel отчета по обороту (admin-only)
export const downloadSavedTurnoverDetailsExcelReport = async (date: string) => {
  try {
    const response = await api.get(`/stats/turnover/excel/reports/${date}`, {
      responseType: "blob",
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;

    const contentDisposition = response.headers["content-disposition"];
    const filename = getFilenameFromContentDisposition(
      contentDisposition,
      `turnover_details_${date}.xlsx`
    );

    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    message.success("Файл отчета успешно скачан!");
  } catch (error) {
    console.error("Error downloading saved turnover report:", error);
    message.error("Ошибка при скачивании отчета");
    throw error;
  }
};

// API функция для генерации и сохранения отчета по обороту сейчас (admin-only)
export const generateAndSaveTurnoverDetailsExcelReport = async (opts?: {
  date?: string; // YYYY-MM-DD
  force?: boolean;
  comparisonDate?: string; // DD.MM.YYYY
}) => {
  try {
    const params: any = {};
    if (opts?.date) params.date = opts.date;
    if (opts?.force) params.force = "true";
    if (opts?.comparisonDate) params.comparisonDate = opts.comparisonDate;

    const response = await api.post("/stats/turnover/excel/reports/generate", null, { params });
    return response.data;
  } catch (error) {
    console.error("Error generating turnover Excel report:", error);
    message.error("Ошибка при генерации отчета");
    throw error;
  }
};

// API функция для создания ручного лога оборота
export const createManualTurnoverLog = async (description: string) => {
  try {
    const response = await api.post("/stats/turnover/logs/create", {
      description,
    });
    return response.data;
  } catch (error) {
    console.error("Error creating manual turnover log:", error);
    throw error;
  }
};
